import { TrendingDown, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';

interface Props {
  allCostData: Record<string, { totalSpend: number; serviceBreakdown?: Record<string, number> }>;
  accounts: { id: string; account_id: string }[];
  isDark: boolean;
}

interface Recommendation {
  priority: 'high' | 'medium' | 'low';
  service: string;
  title: string;
  description: string;
  savingsEstimate: string;
  action: string;
  accountId?: string;
  accountLabel?: string;
}

const RULES: Array<{
  match: (service: string) => boolean;
  threshold: number;
  rec: (cost: number, accountLabel: string) => Recommendation;
}> = [
  {
    match: s => s.includes('EC2-Instance') || s.includes('EC2 - Instance'),
    threshold: 500,
    rec: (cost, label) => ({
      priority: 'high',
      service: 'EC2',
      title: 'Switch to Reserved Instances or Savings Plans',
      description: `You're spending $${cost.toFixed(0)}/mo on EC2 in ${label}. On-demand pricing is 2–3x more expensive than 1-year Reserved Instances for steady workloads.`,
      savingsEstimate: `~$${(cost * 0.38).toFixed(0)}/mo (38% off on-demand)`,
      action: 'Review in AWS Cost Explorer → Reserved Instance Recommendations',
    }),
  },
  {
    match: s => s.includes('RDS'),
    threshold: 300,
    rec: (cost, label) => ({
      priority: 'high',
      service: 'RDS',
      title: 'RDS Reserved Instances available',
      description: `$${cost.toFixed(0)}/mo on RDS in ${label}. RDS Reserved Instances offer up to 40% off on-demand for 1-year commits — no upfront payment option available.`,
      savingsEstimate: `~$${(cost * 0.40).toFixed(0)}/mo (40% off)`,
      action: 'Go to RDS Console → Reserved Instances → Purchase',
    }),
  },
  {
    match: s => s.includes('Redshift'),
    threshold: 500,
    rec: (cost, label) => ({
      priority: 'high',
      service: 'Redshift',
      title: 'Redshift Reserved Nodes can cut costs in half',
      description: `$${cost.toFixed(0)}/mo on Redshift in ${label}. Reserved Nodes offer up to 75% savings for predictable analytics workloads.`,
      savingsEstimate: `~$${(cost * 0.50).toFixed(0)}/mo (50%+ off)`,
      action: 'Go to Redshift Console → Reserved Nodes',
    }),
  },
  {
    match: s => s.includes('Elastic Kubernetes') || s.includes('EKS'),
    threshold: 400,
    rec: (cost, label) => ({
      priority: 'medium',
      service: 'EKS',
      title: 'Use Karpenter + Spot nodes for EKS workloads',
      description: `$${cost.toFixed(0)}/mo on EKS in ${label}. Running batch/stateless workloads on Spot instances via Karpenter typically cuts node costs by 60–70%.`,
      savingsEstimate: `~$${(cost * 0.35).toFixed(0)}/mo (35% of cluster cost)`,
      action: 'Install Karpenter, set spot NodePool for non-critical workloads',
    }),
  },
  {
    match: s => s.includes('Data Transfer'),
    threshold: 200,
    rec: (cost, label) => ({
      priority: 'medium',
      service: 'Data Transfer',
      title: 'VPC Endpoints eliminate NAT Gateway data transfer costs',
      description: `$${cost.toFixed(0)}/mo on data transfer in ${label}. Traffic to S3/DynamoDB through a NAT Gateway costs $0.045/GB — VPC Endpoints are free.`,
      savingsEstimate: `~$${(cost * 0.45).toFixed(0)}/mo (varies by traffic pattern)`,
      action: 'Create VPC Endpoints for S3 and DynamoDB in each VPC',
    }),
  },
  {
    match: s => s.includes('ElastiCache'),
    threshold: 200,
    rec: (cost, label) => ({
      priority: 'medium',
      service: 'ElastiCache',
      title: 'ElastiCache Reserved Nodes — easy win',
      description: `$${cost.toFixed(0)}/mo on ElastiCache in ${label}. Reserved cache nodes for 1 year save 30–40% with no operational changes needed.`,
      savingsEstimate: `~$${(cost * 0.35).toFixed(0)}/mo (35% off)`,
      action: 'ElastiCache Console → Reserved Cache Nodes → Purchase',
    }),
  },
  {
    match: s => s.includes('Glue'),
    threshold: 200,
    rec: (cost, label) => ({
      priority: 'medium',
      service: 'Glue',
      title: 'Optimize Glue DPU usage and job bookmarks',
      description: `$${cost.toFixed(0)}/mo on AWS Glue in ${label}. Glue charges per DPU-hour — oversized worker types and missing job bookmarks (re-processing old data) are common causes of runaway costs.`,
      savingsEstimate: `~$${(cost * 0.30).toFixed(0)}/mo (30% from DPU rightsizing)`,
      action: 'Profile your Glue jobs with Glue job metrics, enable job bookmarks',
    }),
  },
  {
    match: s => s.includes('EMR'),
    threshold: 300,
    rec: (cost, label) => ({
      priority: 'medium',
      service: 'EMR',
      title: 'Run EMR on Spot instances for batch workloads',
      description: `$${cost.toFixed(0)}/mo on EMR in ${label}. EMR task nodes are ideal Spot candidates — they're stateless and EMR handles interruptions automatically.`,
      savingsEstimate: `~$${(cost * 0.55).toFixed(0)}/mo (55–70% for Spot task nodes)`,
      action: 'Configure EMR instance fleet with Spot task nodes',
    }),
  },
  {
    match: s => s.includes('S3'),
    threshold: 400,
    rec: (cost, label) => ({
      priority: 'low',
      service: 'S3',
      title: 'S3 Intelligent-Tiering for infrequently accessed objects',
      description: `$${cost.toFixed(0)}/mo on S3 in ${label}. Objects not accessed for 30 days automatically move to cheaper tiers — no code changes needed.`,
      savingsEstimate: `~$${(cost * 0.25).toFixed(0)}/mo (25% for mixed-access buckets)`,
      action: 'Enable S3 Intelligent-Tiering on your largest buckets',
    }),
  },
  {
    match: s => s.includes('CloudWatch'),
    threshold: 80,
    rec: (cost, label) => ({
      priority: 'low',
      service: 'CloudWatch',
      title: 'Reduce CloudWatch log retention and custom metrics',
      description: `$${cost.toFixed(0)}/mo on CloudWatch in ${label}. Logs stored indefinitely and high-resolution custom metrics are the most common CloudWatch cost drivers.`,
      savingsEstimate: `~$${(cost * 0.40).toFixed(0)}/mo (set 30–90 day log retention)`,
      action: 'Set log group retention policies, audit custom metric usage',
    }),
  },
];

const FONT = '"Plus Jakarta Sans", system-ui, sans-serif';

const PRIORITY_CONFIG = {
  high:   { label: 'High Impact', color: '#DC2626', bg: 'rgba(220,38,38,0.07)',   border: 'rgba(220,38,38,0.2)',   icon: AlertTriangle },
  medium: { label: 'Medium',      color: '#D97706', bg: 'rgba(217,119,6,0.07)',   border: 'rgba(217,119,6,0.2)',   icon: TrendingDown  },
  low:    { label: 'Quick Win',   color: '#16A34A', bg: 'rgba(22,163,74,0.07)',   border: 'rgba(22,163,74,0.2)',   icon: CheckCircle2  },
};

export default function RecommendationsView({ allCostData, accounts, isDark }: Props) {
  const recs: Recommendation[] = [];

  for (const account of accounts) {
    const data = allCostData[account.id];
    if (!data?.serviceBreakdown) continue;
    const label = account.account_id || account.id;

    for (const [service, cost] of Object.entries(data.serviceBreakdown)) {
      for (const rule of RULES) {
        if (rule.match(service) && cost >= rule.threshold) {
          const rec = rule.rec(cost, label);
          // avoid duplicate rule hits for same account
          if (!recs.find(r => r.service === rec.service && r.accountLabel === label)) {
            recs.push({ ...rec, accountId: account.id, accountLabel: label });
          }
        }
      }
    }
  }

  // sort: high → medium → low
  const order = { high: 0, medium: 1, low: 2 };
  recs.sort((a, b) => order[a.priority] - order[b.priority]);

  const totalSavings = recs.reduce((sum, r) => {
    const match = r.savingsEstimate.match(/\$([\d,]+)/);
    return sum + (match ? parseInt(match[1].replace(',', '')) : 0);
  }, 0);

  const textMuted = isDark ? 'rgba(255,255,255,0.38)' : 'rgba(15,22,32,0.45)';
  const cardBg = isDark ? 'rgba(17,24,39,0.8)' : '#ffffff';
  const cardShadow = isDark ? 'none' : '0 1px 3px rgba(15,22,32,0.06), 0 4px 16px rgba(15,22,32,0.04)';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,22,32,0.06)';

  if (recs.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 0', textAlign: 'center', fontFamily: FONT }}>
        <CheckCircle2 className="w-10 h-10 mb-4" style={{ color: '#16A34A', opacity: 0.4 }} />
        <p style={{ fontSize: '14px', fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,22,32,0.55)', marginBottom: '6px' }}>No recommendations yet</p>
        <p style={{ fontSize: '13px', color: textMuted }}>Connect AWS accounts with cost data to see savings opportunities</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', fontFamily: FONT }}>
      {/* Summary banner */}
      <div style={{ borderRadius: '16px', padding: '18px 22px', display: 'flex', alignItems: 'center', gap: '16px', background: isDark ? 'rgba(15,61,46,0.1)' : 'rgba(15,61,46,0.05)', border: `1px solid ${isDark ? 'rgba(15,61,46,0.2)' : 'rgba(15,61,46,0.12)'}` }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: isDark ? 'rgba(15,61,46,0.2)' : 'rgba(15,61,46,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <TrendingDown className="w-5 h-5" style={{ color: '#0F3D2E' }} />
        </div>
        <div>
          <p style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#fff' : '#07281D', marginBottom: '3px' }}>
            {recs.length} savings opportunities identified
          </p>
          <p style={{ fontSize: '12px', color: textMuted }}>
            Potential savings: <span style={{ fontWeight: 700, color: '#16A34A' }}>${totalSavings.toLocaleString()}/mo</span> — prioritize high-impact items first
          </p>
        </div>
      </div>

      {/* Recommendation cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {recs.map((rec, i) => {
          const cfg = PRIORITY_CONFIG[rec.priority];
          const Icon = cfg.icon;
          return (
            <div key={i} style={{ background: cardBg, borderRadius: '16px', padding: '20px 22px', boxShadow: cardShadow, border: cardBorder, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: cfg.bg, border: `1px solid ${cfg.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                    <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '5px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
                        {cfg.label}
                      </span>
                      <span style={{ fontSize: '11px', fontFamily: 'ui-monospace, monospace', padding: '2px 7px', borderRadius: '6px', background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,22,32,0.05)', color: textMuted, border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,22,32,0.06)'}` }}>
                        {rec.accountLabel}
                      </span>
                    </div>
                    <p style={{ fontSize: '14px', fontWeight: 600, color: isDark ? '#fff' : '#07281D' }}>{rec.title}</p>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: '#16A34A' }}>{rec.savingsEstimate}</p>
                  <p style={{ fontSize: '11px', color: textMuted }}>est. savings</p>
                </div>
              </div>

              <p style={{ fontSize: '12px', lineHeight: '1.6', color: textMuted, paddingLeft: '48px' }}>{rec.description}</p>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 14px 10px 48px', borderRadius: '10px', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,22,32,0.025)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,22,32,0.05)'}` }}>
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: textMuted }} />
                <p style={{ fontSize: '12px', color: textMuted }}><span style={{ fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,22,32,0.65)' }}>Action: </span>{rec.action}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
